/* Instructor-series host, mounted by the class console (InstructorClass).
 *
 * Auto-start rule (design doc §1.2): the first visit to any class console.
 * That console may be the populated demo seed or a class created five seconds
 * ago with nobody in it, so the class name and roster counts are passed down
 * and the steps adapt their copy rather than describing the demo.
 * Never auto-starts again once dismissed or completed; "? Tour" restarts.
 * Instrumented to ActivityLog without a teamId (console events are class-level). */
import { useEffect } from 'react';
import { useAuth } from '../hooks/queries';
import { api } from '../lib/api';
import { useTutorial } from './useTutorial';
import { TutorialHost } from './TutorialHost';
import { instructorSeries } from './steps.instructor';
import { shapeOf, type TutCtx } from './types';

export function InstructorTutorial({ tab, drilldownOpen, className, studentCount, playedCount }: {
  tab: string;
  drilldownOpen: boolean;
  /** Name of the class on screen — the copy names it instead of the demo seed. */
  className: string;
  studentCount: number;
  playedCount: number;
}) {
  const { user } = useAuth();
  const hydrated = useTutorial((z) => z.hydrated);
  const active = useTutorial((z) => z.active);
  const signals = useTutorial((z) => z.signals);

  useEffect(() => {
    if (user) useTutorial.getState().hydrate(user.tutorialState, user.id);
  }, [user]);

  useEffect(() => {
    if (hydrated && useTutorial.getState().active == null) {
      useTutorial.getState().start('instructor', shapeOf(instructorSeries));
    }
  }, [hydrated]);

  useEffect(() => {
    useTutorial.getState().setLogSink((e) => {
      api.logActivity({ type: 'tutorial', payload: { ...e } }).catch(() => { /* fire-and-forget */ });
    });
    return () => useTutorial.getState().setLogSink(null);
  }, []);

  if (active !== 'instructor') return null;

  const ctx: TutCtx = {
    tab,
    draft: {},
    game: null,
    maxRounds: 16,
    refPrice: '',
    signals,
    resultOpen: false,
    submitted: false,
    drilldownOpen,
    className,
    studentCount,
    playedCount,
  };
  return <TutorialHost series={instructorSeries} ctx={ctx} />;
}
